// src/publish/downloads.js — the download catalogue and builder of a history entry.
//
// «Copia per pubblicare» and «Scarica» read the piece list of an entry
// (pieces/index.js), never content.full_text, so nothing downloaded carries
// leftover markup, run data or the brief. The brief has its own file.
//
//   DOWNLOAD_IDS                     the nine stable ids below
//   downloadOptionsForEntry(entry)   → [{ id, label, ext, group }] the menu rows this
//                                    entry offers, in catalogue order; label from
//                                    outputs:downloads.<id>, group 'ready' | 'all' | 'extra'
//   buildDownload(entry, id)         → Promise<{ blob, filename }>; an unknown id rejects
//                                    with Error('UNKNOWN_DOWNLOAD') (a code, never prose)
//   downloadBlob(blob, filename)     saves a built file through a temporary <a download>
//                                    (the one save path of the generator's history and
//                                    the console's run detail): the anchor is appended
//                                    to document.body for the click and removed at
//                                    once; the object URL is revoked REVOKE_DELAY_MS
//                                    later, never synchronously — Firefox and Safari
//                                    abort a large download whose URL is revoked
//                                    before the fetch starts
//   primaryText(pieces)              → the paste-ready text: the primary piece, its
//                                    "[N]" markers kept only when the entry also has a
//                                    'sources' piece (the reader can look them up) —
//                                    the one text of the clipboard (the card's «Copia
//                                    per pubblicare» counter, components/outputs/pieces/
//                                    ReadyToPublish.jsx) and of ready-txt
//   allPiecesText(pieces)            → every piece in one plain text, "=== Label ===" per
//                                    piece (piecesToPlain, no title) — what the card's
//                                    «Copia tutto» puts on the clipboard as text/plain:
//                                    the same text the per-piece «Copia» buttons copy
//                                    one at a time, metadata pieces included
//   allPiecesHtml(pieces)            → the same list as an HTML fragment (<h2> label +
//                                    blocks per piece), the text/html twin of «Copia
//                                    tutto»; '' for an empty list
//   copyAllHtml(entry, pieces)       → what «Copia tutto» puts on the clipboard as
//                                    text/html: '' for a social format (plain text
//                                    only — LinkedIn flattens pasted HTML), else
//                                    allPiecesHtml(pieces)
//   documentTitleForEntry(entry, pieces?) → the title at the top of every "all pieces"
//                                    file: the content's own title (content.title, else
//                                    the primary piece's first heading), never the
//                                    brief's topic while the content names itself;
//                                    `pieces` may be passed when the caller already
//                                    holds the list (else the cached piecesForEntry)
//
// Catalogue (ids, what they hold, which formats offer them):
//   ready-txt    the primary piece, plain text         every format
//   ready-html   the primary piece, clean HTML document rich formats only
//   all-txt      every piece, plain text               every format
//   all-md       every piece, Markdown                 newsletter, blog (+ generic/legacy)
//   all-html     every piece, standalone HTML          rich formats
//   all-docx     every piece, real .docx (serialize/docx.js, loaded on demand)
//   all-pdf      every piece, text PDF (serialize/pdf.js, loaded on demand)
//   slides-csv   one row per slide (Canva / PowerPoint) linkedin-carousel only
//   brief-json   { version: 1, entryId, timestamp, mode, params } — what
//                utils/importParamsFromFile.js reads back into Step 1
// Rich formats: linkedin-newsletter, blog-seo, email-email-classic,
// landing-landing. Docx/pdf: those four plus youtube-post and linkedin-carousel.
// The catalogue key is the key of the builder the entry dispatches to
// (pieces/index.js :: builderKeyFor — contentFormat first, then platform), so
// the files always match the pieces: an entry with platform null and
// contentFormat 'seo' gets the blog's pieces and the blog's rows. A legacy
// entry (dispatched to the generic builder) gets the generic set; an entry with
// no pieces offers brief-json alone.
//
// File names: entryFileStem(entry) (fileName.js) + the extension; the
// paste-ready files add "_pronto-da-pubblicare", the CSV "_slide", the brief
// "_brief". Content files hold ONLY the pieces: a title, then one heading per
// piece — no marker, no run data (old files with the in-text marker still
// import; that reader is untouched).

import i18n from '../i18n/index.js';
import { entryFileStem } from './fileName.js';
import { runsToText } from './blocks.js';
import { piecesForEntry, builderKeyFor, primaryPiece, pieceText, pieceHtml } from './pieces/index.js';
import { piecesToPlain } from './serialize/plain.js';
import { piecesToMarkdown, sourceText } from './serialize/markdown.js';
import { piecesToHtml, htmlDocument, pieceToHtmlFragment } from './serialize/html.js';
import { slidesToCsv } from './serialize/csv.js';

export const DOWNLOAD_IDS = Object.freeze([
  'ready-txt', 'ready-html', 'all-txt', 'all-md', 'all-html', 'all-docx', 'all-pdf', 'slides-csv', 'brief-json',
]);

export const MIME = Object.freeze({
  txt: 'text/plain;charset=utf-8',
  md: 'text/markdown;charset=utf-8',
  html: 'text/html;charset=utf-8',
  csv: 'text/csv;charset=utf-8',
  json: 'application/json;charset=utf-8',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  pdf: 'application/pdf',
});

const RICH_FORMATS = ['linkedin-newsletter', 'blog-seo', 'email-email-classic', 'landing-landing'];
const DOCUMENT_FORMATS = ['youtube-post', 'linkedin-carousel', ...RICH_FORMATS];
const SOCIAL_FORMATS = ['linkedin-post', 'facebook-post', 'instagram-post', 'pinterest-post'];
const KNOWN_FORMATS = [...SOCIAL_FORMATS, ...DOCUMENT_FORMATS];
const MARKDOWN_FORMATS = ['linkedin-newsletter', 'blog-seo', 'generic'];
const CSV_FORMATS = ['linkedin-carousel'];

// Catalogue order = menu order. `suffix` is appended to the stem before ".ext";
// `offered(format)` says whether the format gets the row (empty piece lists are
// handled before this is consulted).
const CATALOGUE = [
  { id: 'ready-txt', ext: 'txt', group: 'ready', suffix: '_pronto-da-pubblicare', offered: () => true },
  { id: 'ready-html', ext: 'html', group: 'ready', suffix: '_pronto-da-pubblicare', offered: (f) => RICH_FORMATS.includes(f) },
  { id: 'all-txt', ext: 'txt', group: 'all', suffix: '', offered: () => true },
  { id: 'all-md', ext: 'md', group: 'all', suffix: '', offered: (f) => MARKDOWN_FORMATS.includes(f) },
  { id: 'all-html', ext: 'html', group: 'all', suffix: '', offered: (f) => RICH_FORMATS.includes(f) },
  { id: 'all-docx', ext: 'docx', group: 'all', suffix: '', offered: (f) => DOCUMENT_FORMATS.includes(f) },
  { id: 'all-pdf', ext: 'pdf', group: 'all', suffix: '', offered: (f) => DOCUMENT_FORMATS.includes(f) },
  { id: 'slides-csv', ext: 'csv', group: 'extra', suffix: '_slide', offered: (f) => CSV_FORMATS.includes(f) },
  { id: 'brief-json', ext: 'json', group: 'extra', suffix: '_brief', offered: () => true },
];

const BRIEF_ONLY = CATALOGUE.filter((row) => row.id === 'brief-json');

/** The catalogue key of an entry: the key of the builder it dispatches to, else 'generic'. */
export function downloadFormatKey(entry) {
  const key = builderKeyFor(entry);
  return KNOWN_FORMATS.includes(key) ? key : 'generic';
}

const downloadLabel = (id) => i18n.t(`outputs:downloads.${id}`);

export function downloadOptionsForEntry(entry) {
  const pieces = piecesForEntry(entry);
  const format = downloadFormatKey(entry);
  const rows = pieces.length ? CATALOGUE.filter((row) => row.offered(format)) : BRIEF_ONLY;
  return rows.map(({ id, ext, group }) => ({ id, label: downloadLabel(id), ext, group }));
}

/** The file name of a download id for an entry (stem + suffix + ".ext"); null for an unknown id. */
export function downloadFileName(entry, id) {
  const row = CATALOGUE.find((r) => r.id === id);
  return row ? `${entryFileStem(entry)}${row.suffix}.${row.ext}` : null;
}

// ─── Title ───────────────────────────────────────────────────────────────────

function firstHeadingText(pieces) {
  for (const p of pieces) {
    for (const b of Array.isArray(p?.blocks) ? p.blocks : []) {
      if (b?.type === 'heading') {
        const t = runsToText(b.runs).trim();
        if (t) return t;
      }
    }
  }
  return '';
}

function firstLine(s) {
  return String(s ?? '').split('\n').map((l) => l.trim()).find(Boolean) || '';
}

const nonEmpty = (v) => (typeof v === 'string' && v.trim() ? v.trim() : '');

/**
 * The content's own title first — content.title (the backend mirrors the
 * article H1, the email subject, the landing headline there), else the first
 * heading of the primary piece — then results.topic (the brief), the first
 * heading of any piece, the first line of the primary piece, the mode's name,
 * and «Contenuto» last. A file is titled by what it holds, not by what was asked.
 */
export function documentTitleForEntry(entry, prebuilt) {
  const contentTitle = nonEmpty(entry?.results?.content?.title);
  if (contentTitle) return contentTitle;
  const pieces = Array.isArray(prebuilt) ? prebuilt : piecesForEntry(entry);
  const primary = primaryPiece(pieces);
  const primaryHeading = primary ? firstHeadingText([primary]) : '';
  if (primaryHeading) return primaryHeading;
  const topic = nonEmpty(entry?.results?.topic);
  if (topic) return topic;
  const heading = firstHeadingText(pieces);
  if (heading) return heading;
  const line = primary && primary.kind !== 'source' ? firstLine(pieceText(primary)) : '';
  if (line) return line;
  const modeName = nonEmpty(entry?.modeConfig?.name);
  if (modeName) return modeName;
  return i18n.t('outputs:downloads.untitled');
}

// ─── Copy ────────────────────────────────────────────────────────────────────

/** Whether the piece list carries the cited sources (then "[N]" means something to the reader). */
const hasSources = (pieces) => pieces.some((p) => p && p.id === 'sources');

/**
 * The paste-ready text of a piece list: the primary piece's plain text, with
 * its "[N]" citation markers only when a 'sources' piece lists what they refer
 * to (the same rule piecesToPlain applies to the "all pieces" .txt); a source
 * piece gives its verbatim code. '' when there is no primary piece.
 */
export function primaryText(pieces) {
  const piece = primaryPiece(pieces);
  if (!piece) return '';
  return piece.kind === 'source' ? sourceText(piece) : pieceText(piece, { cites: hasSources(pieces) });
}

/** Every piece as one plain text, labelled per piece (see piecesToPlain); '' for no pieces. */
export function allPiecesText(pieces) {
  return piecesToPlain(Array.isArray(pieces) ? pieces : []);
}

/** Every piece as one HTML fragment, labelled per piece; '' for no pieces. */
export function allPiecesHtml(pieces) {
  const list = Array.isArray(pieces) ? pieces.filter(Boolean) : [];
  return list.map((p) => pieceToHtmlFragment(p)).join('\n');
}

/**
 * The html the card's «Copia tutto» puts on the clipboard: '' (text/plain
 * only) for a social format — LinkedIn's composer takes a text/html payload
 * and drops its paragraph breaks, while plain text keeps them — and
 * allPiecesHtml(pieces) for every other format (Word / Docs / a CMS editor
 * keep headings, bold and lists from it). The gate is the format, not "has a
 * rich piece": the Carousel and the YouTube post have none, yet their
 * «Copia tutto» goes to a document.
 */
export function copyAllHtml(entry, pieces) {
  return SOCIAL_FORMATS.includes(builderKeyFor(entry)) ? '' : allPiecesHtml(pieces);
}

// ─── Build ───────────────────────────────────────────────────────────────────

function briefPayload(entry) {
  const ts = entry?.timestamp;
  const timestamp = ts instanceof Date
    ? (Number.isNaN(ts.getTime()) ? null : ts.toISOString())
    : (typeof ts === 'string' || typeof ts === 'number' ? ts : null);
  return {
    version: 1,
    entryId: entry?.id ?? null,
    timestamp,
    mode: entry?.mode ?? null,
    params: entry?.params && typeof entry.params === 'object' ? entry.params : {},
  };
}

/** The text of a download id (every id but docx and pdf); null for an unknown id. */
function buildText(entry, id, pieces, title) {
  const primary = primaryPiece(pieces);
  switch (id) {
    case 'ready-txt': return primaryText(pieces);
    case 'ready-html': return htmlDocument({ title, body: primary ? pieceHtml(primary) : '' });
    case 'all-txt': return piecesToPlain(pieces, { title });
    case 'all-md': return piecesToMarkdown(pieces, { title });
    case 'all-html': return piecesToHtml(pieces, { title });
    case 'slides-csv': return slidesToCsv(pieces);
    case 'brief-json': return `${JSON.stringify(briefPayload(entry), null, 2)}\n`;
    default: return null;
  }
}

export async function buildDownload(entry, id) {
  const row = CATALOGUE.find((r) => r.id === id);
  if (!row) throw new Error('UNKNOWN_DOWNLOAD');
  const filename = downloadFileName(entry, id);
  const pieces = piecesForEntry(entry);
  const title = documentTitleForEntry(entry, pieces);

  if (id === 'all-docx') {
    const { piecesToDocx } = await import('./serialize/docx.js');
    return { blob: await piecesToDocx(pieces, { title }), filename };
  }
  if (id === 'all-pdf') {
    const { piecesToPdf } = await import('./serialize/pdf.js');
    return { blob: await piecesToPdf(pieces, { title }), filename };
  }
  const text = buildText(entry, id, pieces, title);
  return { blob: new Blob([text], { type: MIME[row.ext] }), filename };
}

// ─── Save ────────────────────────────────────────────────────────────────────

/** How long the object URL outlives the click: the browser must have started the fetch. */
export const REVOKE_DELAY_MS = 1000;

/**
 * Hands a built file to the browser's download flow via a temporary <a download>:
 * in the document for the click (a detached anchor is ignored by some browsers),
 * removed at once, its object URL revoked after REVOKE_DELAY_MS.
 */
export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), REVOKE_DELAY_MS);
}
